import React, {useState} from "react";
import {useAuth} from "../util/auth";
import sessionInfo from "../util/sessionInfo";
import {Story, story} from "../utils/story";
import {observer} from "mobx-react-lite";
import {useRouter} from "next/router";
import {Box, Button, InputBase, Paper, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import PayMe from "./PayMe";
import {SignupPopup} from "./SignupPopup";
import ShareButton from "./ShareButton";
import track from "../util/frontend_analytics";
import {forgetWall, rememberWall, takeRestoredText} from "../util/authReturn";
import MinimalLoader from "./MinimalLoader";
import CharacterAvatar from "./CharacterAvatar";
import {Character, characterStore, MAX_IN_STORY} from "../utils/characters";

// Continuations a visitor can write before signing up (counted in the browser only)
const FREE_ANONYMOUS_SECTIONS = 3;

const useStyles = makeStyles((theme) => ({
    // a full card right where the story ends, so the next turn is impossible to miss
    card: {
        maxWidth: 680,
        margin: theme.spacing(2, "auto", 6),
        padding: theme.spacing(2.25, 2.5),
        borderRadius: 20,
        backgroundColor: "#21161F",
        border: "1px solid rgba(236, 122, 150, 0.35)",
        boxShadow: "0 18px 50px rgba(0, 0, 0, 0.45)",
        [theme.breakpoints.down("xs")]: {
            margin: theme.spacing(1.5, 2.5, 4),
            padding: theme.spacing(2, 1.75),
        },
    },
    title: {
        fontFamily: "Lora, Georgia, serif",
        fontWeight: 600,
        fontSize: "1.3rem",
        lineHeight: 1.2,
        [theme.breakpoints.down("xs")]: {
            fontSize: "1.2rem",
        },
    },
    hint: {
        marginTop: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: "0.9rem",
    },
    error: {
        marginTop: theme.spacing(1),
        color: theme.palette.error.main,
        fontSize: "0.9rem",
    },
    input: {
        display: "flex",
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.75),
        borderRadius: 14,
        backgroundColor: "#120B10",
        border: "1px solid rgba(255, 220, 232, 0.18)",
        fontSize: "1rem",
        lineHeight: 1.5,
        transition: theme.transitions.create(["border-color", "box-shadow"]),
        "&$focused": {
            borderColor: "rgba(236, 122, 150, 0.7)",
            boxShadow: "0 0 0 3px rgba(236, 122, 150, 0.16)",
        },
        // 16px or more keeps iOS from zooming into the field
        "& textarea": {
            fontSize: 16,
        },
    },
    focused: {},
    inputWrap: {
        position: "relative",
    },
    // the @ picker opens just under the field
    mentionMenu: {
        position: "absolute",
        left: 0,
        top: "100%",
        zIndex: 10,
        marginTop: theme.spacing(0.5),
        minWidth: 220,
        maxWidth: "100%",
        padding: theme.spacing(0.5),
        borderRadius: 14,
    },
    mentionItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.25),
        width: "100%",
        padding: theme.spacing(0.75, 1),
        border: "none",
        borderRadius: 10,
        background: "none",
        color: theme.palette.text.primary,
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
    },
    mentionActive: {
        backgroundColor: theme.palette.action.selected,
    },
    actions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1.5),
    },
    submitButton: {
        minWidth: 180,
        height: 46,
        fontSize: "1rem",
        padding: theme.spacing(0, 3),
        [theme.breakpoints.down("xs")]: {
            flex: 1,
            minWidth: 0,
        },
        "&$disabled": {
            backgroundColor: "rgba(236, 122, 150, 0.22)",
            color: "rgba(245, 236, 239, 0.6)",
        },
    },
    disabled: {},
    buttonContent: {
        display: "flex",
        alignItems: "center",
    },
    loaderSpacing: {
        marginLeft: theme.spacing(1),
        fontWeight: 600,
    },
}));

// The @ being typed, from the @ up to the caret
interface Mention {
    start: number
    query: string
}

// A character picked with @: their ID goes with the write while their name is still in the text
interface Pick {
    characterId: string
    name: string
}

const MENTION_MATCHES = 6;

export const UserInputCard = observer(function UserInputCard({waitingUser}: { waitingUser: boolean }) {
    const classes = useStyles();
    const [text, setText] = useState('');
    const [mention, setMention] = useState<Mention | null>(null);
    const [activeMatch, setActiveMatch] = useState(0);
    const [picks, setPicks] = useState<Pick[]>([]);
    const dismissedAt = React.useRef<number>(-1);
    const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [paymeActive, setPaymeActive] = useState(false);
    const [signupRequireActive, setSignupRequired] = useState(false);
    const auth = useAuth();
    const submitButtonRef = React.useRef<HTMLButtonElement>(null);

    // the wall closed, or the reader moved on without going to Google: a later sign-in doesn't come back to this story
    const router = useRouter();
    React.useEffect(() => {
        if (!signupRequireActive) return;
        const leave = () => setSignupRequired(false);
        router.events.on('routeChangeStart', leave);
        return () => {
            router.events.off('routeChangeStart', leave);
            forgetWall();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signupRequireActive]);

    // back from signing up with Google mid-story: the line the reader had typed, once their story has loaded
    const storyId = story.id;
    React.useEffect(() => {
        const restored = takeRestoredText(storyId);
        if (restored) setText(restored);
    }, [storyId]);

    const matches: Character[] = mention ? characterStore.usable
        .filter((character) => character.name.toLowerCase().includes(mention.query.toLowerCase()))
        .slice(0, MENTION_MATCHES) : [];
    const mentionOpen = matches.length > 0;

    // Open the picker while the caret follows an @ that starts a word, until Escape or nothing matches
    function updateMention(value: string, caret: number) {
        const before = value.slice(0, caret);
        const at = before.lastIndexOf('@');
        const query = before.slice(at + 1);
        if (at === -1 || (at > 0 && !/\s/.test(before[at - 1])) || query.length > 40 || /[\n@]/.test(query)) {
            dismissedAt.current = -1;
            setMention(null);
            return;
        }
        if (dismissedAt.current === at || !characterStore.enabled) {
            setMention(null);
            return;
        }
        setMention({start: at, query});
        setActiveMatch(0);
    }

    function pickCharacter(character: Character) {
        if (!mention) {
            return;
        }
        const inserted = character.name + ' ';
        const next = text.slice(0, mention.start) + inserted + text.slice(mention.start + 1 + mention.query.length);
        const caret = mention.start + inserted.length;
        // a story's cast holds at most MAX_IN_STORY; characters already in it don't take a new place
        const inStory = (id: string) => story.characters.some((member) => member.characterId === id);
        const joining = picks.filter((pick) => !inStory(pick.characterId)).length;
        const isNew = !picks.some((pick) => pick.characterId === character.characterId);
        if (isNew && !inStory(character.characterId) && story.characters.length + joining >= MAX_IN_STORY) {
            setMention(null);
            setError(`A story can have up to ${MAX_IN_STORY} characters.`);
            return;
        }
        setText(next);
        setMention(null);
        setError('');
        if (isNew) {
            setPicks([...picks, {characterId: character.characterId, name: character.name}]);
        }
        window.requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(caret, caret);
            }
        });
    }

    function handleMentionKeys(event: React.KeyboardEvent) {
        // Cmd+Enter (Ctrl+Enter on Windows) continues the story
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            if (!processing && !story.underEdit && story.sections.length > 0) {
                generateSection();
            }
            return;
        }
        if (!mentionOpen || !mention) {
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActiveMatch((activeMatch + step + matches.length) % matches.length);
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            pickCharacter(matches[Math.min(activeMatch, matches.length - 1)]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            dismissedAt.current = mention.start;
            setMention(null);
        }
    }

    // to the very bottom once the page has drawn, and again a moment later once pictures have taken their space
    function scrollToBottom() {
        const go = () => window.scrollTo({top: document.documentElement.scrollHeight, behavior: 'smooth'});
        requestAnimationFrame(go);
        setTimeout(go, 600);
    }

    // once the new part is in, it starts at the top of the screen (the reader's own line first), ready to read down
    function scrollToNewPart() {
        requestAnimationFrame(() => {
            const parts = document.querySelectorAll('[data-story-part]');
            const last = parts[parts.length - 1] as HTMLElement | undefined;
            if (!last) {
                scrollToBottom();
                return;
            }
            const margin = 24;
            window.scrollTo({top: last.getBoundingClientRect().top + window.scrollY - margin, behavior: 'smooth'});
        });
    }

    async function generateSection() {
        const usedCredits = sessionInfo.usedCredits;
        const notSignedUp = (!auth.user) && (usedCredits >= FREE_ANONYMOUS_SECTIONS);
        if (notSignedUp) {
            track("story_signup_required", {theme: story.theme});
            rememberWall(story.id, text);
            setSignupRequired(true);
            return;
        }

        if (auth.user && (auth.user.availableCredits <= 0)) {
            setPaymeActive(true);
            track("story_credit_exhausted", {theme: story.theme});
            return;
        }

        sessionInfo.useCredit();
        const characterIds = picks.filter((pick) => text.includes(pick.name)).map((pick) => pick.characterId)
            .slice(0, MAX_IN_STORY);
        const draft = {text, picks};
        setError('');
        setPicks([]);
        setMention(null);
        setText('');
        setProcessing(true);
        story.setProcessing(true);
        scrollToBottom();

        const prevSectionId = story.sections[story.sections.length - 1].id;
        const quality = "high";
        let result: true | string | undefined;
        try {
            let retry = false;
            result = await story.generateNewSection(text, prevSectionId, retry, quality, characterIds);
        } catch (e) {
            console.log(e);
            await new Promise((r) => setTimeout(r, 15000));
            console.log('retrying after 15s...');
            try {
                let retry = true;
                result = await story.generateNewSection(text, prevSectionId, retry, quality, characterIds);
            } catch (e) {
                console.log(e);
                await new Promise((r) => setTimeout(r, 20000));
                console.log('retrying after 20s...');
                try {
                    let retry = true;
                    result = await story.generateNewSection(text, prevSectionId, retry, quality, characterIds);
                } catch (e) {
                    console.log(e);
                    await new Promise((r) => setTimeout(r, 25000));
                    console.log('final retry after 25s...');
                    try {
                        let retry = true;
                        result = await story.generateNewSection(text, prevSectionId, retry, quality, characterIds);
                    } catch (e) {
                        console.log(e);
                        console.log('failed to generate section after 60s total wait time');
                    }
                }
            }
        }
        setProcessing(false);
        story.setProcessing(false);
        // nothing was written: the reader's text and picks come back, with the reason, unless they've typed something new
        if (result !== true) {
            if (!(inputRef.current?.value || '').trim()) {
                setText(draft.text);
                setPicks(draft.picks);
            }
            setError(typeof result === 'string' ? result : "Couldn't continue the story. Please try again.");
        }
        scrollToNewPart();
    }

    const StoryViewerButton = observer(({story}: { story: Story }) => {
        return (
            <Button
                ref={submitButtonRef}
                variant={'contained'}
                color={'primary'}
                disableElevation
                classes={{root: classes.submitButton, disabled: classes.disabled}}
                disabled={processing || story.underEdit || story.sections.length === 0}
                title={typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘ Enter' : 'Ctrl Enter'}
                onClick={async () => {
                    await generateSection();
                }}
            >
                {processing ? (
                    <Box className={classes.buttonContent}>
                        <MinimalLoader compact/>
                        <span className={classes.loaderSpacing}>Writing</span>
                    </Box>
                ) : (
                    'Continue'
                )}
            </Button>
        );
    });

    return waitingUser ? (
        <>
            {paymeActive && (
                <PayMe
                    open={paymeActive}
                    dismissCallback={() => {
                        setPaymeActive(false);
                    }}
                    runOut={true}
                />
            )}
            {signupRequireActive && (
                <SignupPopup
                    setSignupModal={setSignupRequired}
                    signupModal={signupRequireActive}
                />
            )}
            <Box className={classes.card}>
                <Typography component="h2" className={classes.title}>
                    What happens next?
                </Typography>
                <Typography className={classes.hint}>
                    You decide. A sentence is enough, or leave it empty to be surprised.
                    {characterStore.usable.length > 0 && ' Type @ to bring in one of your characters.'}
                </Typography>
                {error && <Typography className={classes.error}>{error}</Typography>}
                <Box className={classes.inputWrap}>
                    <InputBase
                        classes={{root: classes.input, focused: classes.focused}}
                        multiline
                        fullWidth
                        minRows={2}
                        maxRows={8}
                        inputRef={inputRef}
                        onChange={(e) => {
                            setText(e.target.value);
                            updateMention(e.target.value, (e.target as HTMLTextAreaElement).selectionStart || 0);
                        }}
                        onKeyDown={handleMentionKeys}
                        onBlur={() => setMention(null)}
                        value={text}
                        placeholder={'She pulls him into the empty hallway...'}
                        inputProps={{'aria-label': 'What happens next?'}}
                    />
                    {mentionOpen && (
                        <Paper elevation={8} className={classes.mentionMenu} role="listbox" aria-label="Your characters">
                            {matches.map((character, index) => (
                                <button
                                    type="button"
                                    key={character.characterId}
                                    role="option"
                                    aria-selected={index === activeMatch}
                                    className={`${classes.mentionItem} ${index === activeMatch ? classes.mentionActive : ''}`}
                                    onMouseDown={(event) => event.preventDefault()}  // keep the field focused
                                    onMouseEnter={() => setActiveMatch(index)}
                                    onClick={() => pickCharacter(character)}
                                >
                                    <CharacterAvatar name={character.name} portrait={character.portrait} size={26}/>
                                    <span>{character.name}</span>
                                </button>
                            ))}
                        </Paper>
                    )}
                </Box>
                <Box className={classes.actions}>
                    <ShareButton link={`www.mytales.io/story/${story.id}`} compact/>
                    <StoryViewerButton story={story}/>
                </Box>
            </Box>
        </>
    ) : null;
});
